<template>
  <div class="flex gap-[10px] flex-col">
    <TagInputWithDropdownList
      ref="countriesDropdown"
      :label="$t('appliedFilters.countries')"
      :empty-label="$t('appliedFilters.searchForCountries')"
      :list="countries"
      :selected-tags="selectedCountriesTags"
      data-text="countriesDropdown"
      @add="(tag: ListItem) => addCountryTag(tag)"
      @remove="(id: Number) => removeCountryTag(selectedCountriesTags, id)"
    />
    <TagInputWithDropdownList
      ref="languagesDropdown"
      :label="$t('appliedFilters.languages')"
      :empty-label="$t('appliedFilters.searchForLanguages')"
      :list="languages"
      :selected-tags="selectedLanguagesTags"
      data-text="languagesDropdown"
      @add="(tag: ListItem) => addLanguageTag(tag)"
      @remove="(id: Number) => removeLanguageTag(selectedLanguagesTags, id)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, Ref, ref, onMounted, watch, onBeforeUnmount } from "vue";
import { useNuxtApp } from "nuxt/app";
import TagInputWithDropdownList from "~/components/shared/TagInputWithDropdownList.vue";
import { isIdPresent } from "~/utils/IsIdPresent";
import { removeObjectById } from "~/utils/RemoveObjectById";
import ListItem from "~/types/ListItemType";
import allLanguages from "~/constants/languages";
import { useSearchFiltersStore } from "~/store/searchFilter";
import { useOrgStore } from "@/store/org";
import Emitter from "~/types/Emitter";

const orgStore = useOrgStore();
const searchFilterStore = useSearchFiltersStore();

const { $bus }: Emitter = useNuxtApp();
const selectedCountriesTags: Ref<Array<ListItem>> = ref([]);
const selectedLanguagesTags: Ref<Array<ListItem>> = ref([]);
const countriesDropdown: Ref = ref(null);
const languagesDropdown: Ref = ref(null);

const emit = defineEmits(["performUpdate"]);

const countries: Ref<Array<ListItem>> = computed(() => {
  return orgStore.getAllowedCountries;
});
const languages: Ref<Array<ListItem>> = ref(allLanguages);

const addCountryTag = (tag: ListItem) => {
  if (!isIdPresent(selectedCountriesTags.value, tag.id)) {
    selectedCountriesTags.value.push(tag);
    updateCountriesFilter();
  }
};

const removeCountryTag = (list: Array<ListItem>, id: Number) => {
  selectedCountriesTags.value = removeObjectById(list, id);
  updateCountriesFilter();
};

const updateCountriesFilter = () => {
  const selectedCountriesString = selectedCountriesTags.value
    .map((country) => country.iso)
    .join("|");

  if (searchFilterStore?.currentSearchFilter) {
    searchFilterStore.currentSearchFilter.filter_countries =
      selectedCountriesString;
  }

  emit("performUpdate");
};

const addLanguageTag = (tag: ListItem) => {
  if (!isIdPresent(selectedLanguagesTags.value, tag.id)) {
    selectedLanguagesTags.value.push(tag);
    updateLanguagesFilter();
  }
};

const removeLanguageTag = (list: Array<ListItem>, id: Number) => {
  selectedLanguagesTags.value = removeObjectById(list, id);
  updateLanguagesFilter();
};

const updateLanguagesFilter = () => {
  const selectedLanguagesString = selectedLanguagesTags.value
    .map((language) => language.value)
    .join("|");

  if (searchFilterStore?.currentSearchFilter) {
    searchFilterStore.currentSearchFilter.filter_languages =
      selectedLanguagesString;
  }

  emit("performUpdate");
};

onMounted(() => {
  if (searchFilterStore?.currentSearchFilter) {
    const selectedCountries =
      searchFilterStore.currentSearchFilter.filter_countries.split("|");
    selectCountries(selectedCountries);

    const selectedLanguages =
      searchFilterStore.currentSearchFilter.filter_languages.split("|");
    selectLanguages(selectedLanguages);
  }
});

// Watch for changes in getCurrentSearchFilter
watch(
  () => searchFilterStore.getCurrentSearchFilter,
  (newValue) => {
    const selectedCountries = newValue.filter_countries.split("|");
    selectCountries(selectedCountries);

    const selectedLanguages = newValue.filter_languages.split("|");
    selectLanguages(selectedLanguages);
  },
);

const selectCountries = (selectedCountries: Array<string>) => {
  selectedCountriesTags.value = [];

  selectedCountries.forEach((selectedCountry: string) => {
    const matchingCounty = countries.value.find(
      (country) => country.iso === selectedCountry,
    );
    if (matchingCounty) {
      selectedCountriesTags.value.push(matchingCounty);
    }
  });
};

const selectLanguages = (selectedLanguages: Array<string>) => {
  selectedLanguagesTags.value = [];

  selectedLanguages.forEach((selectedLanguage: string) => {
    const matchingLanguage = languages.value.find(
      (language) => language.value === selectedLanguage,
    );
    if (matchingLanguage) {
      selectedLanguagesTags.value.push(matchingLanguage);
    }
  });
};

$bus.$on("CustomDrawer:click", (e: Array<any>) => {
  if (e[1]) {
    countriesDropdown.value?.collapse();
    languagesDropdown.value?.collapse();
    return;
  }
  if (
    ![countriesDropdown, languagesDropdown].some((r) =>
      r.value?.$el.contains(e[0].target),
    )
  ) {
    countriesDropdown.value?.collapse();
    languagesDropdown.value?.collapse();
  }
});

onBeforeUnmount(() => {
  $bus.$off("CustomDrawer:click");
});
</script>
